import React, { useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Loader2,
} from 'lucide-react';
import { DataGridPagination } from '../grid/DataGridPagination';

export interface EriciusColumnConfig<T = any> {
  label: React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string;
  className?: string;
  headerClassName?: string;
  colRenderer?: (row: T, index: number) => React.ReactNode;
  render?: (row: T, index: number) => React.ReactNode;
  onColClick?: (row: T, e: React.MouseEvent) => void;
  onCellClick?: (row: T, e: React.MouseEvent) => void;
}

export type EriciusColDefinition<T = any> =
  | { [key: string]: EriciusColumnConfig<T> }
  | ({
      key?: string;
      id?: string;
    } & EriciusColumnConfig<T>);

export interface EriciusTablePagination {
  page: number;
  pageSize: number;
  total: number;
}

export interface EriciusTableProps<T extends Record<string, any> = Record<string, any>> {
  /** Column definitions (array of column objects or keyed configurations) */
  columns?: EriciusColDefinition<T>[];
  cols?: EriciusColDefinition<T>[];

  /** Data items to render */
  data: T[];

  /** Key extractor for rows (defaults to row.id or index) */
  getRowId?: (row: T, index: number) => string;

  /** Row click callback — clicking anywhere on row triggers this */
  onRowClick?: (row: T, index: number) => void;

  /** Custom row class generator */
  rowClassName?: (row: T, index: number) => string;

  /** Pagination details */
  pagination?: EriciusTablePagination;
  onPageChange?: (newPage: number) => void;
  onPagePrev?: () => void;
  onPageNext?: () => void;
  onPageSizeChange?: (newSize: number) => void;
  onPerPage?: (newSize: number) => void;
  pageSizeOptions?: number[];

  /** Sorting */
  sorting?: { field: string; dir: 'asc' | 'desc' };
  onSortChange?: (field: string, dir: 'asc' | 'desc') => void;

  /** Loading and empty states */
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: React.ReactNode;

  /** Optional min-width for horizontal scroll */
  minWidth?: string;
  className?: string;
}

interface NormalizedColumn<T> {
  key: string;
  label: React.ReactNode;
  sortable: boolean;
  align: 'left' | 'center' | 'right';
  width?: string;
  className?: string;
  headerClassName?: string;
  render: (row: T, index: number) => React.ReactNode;
  onCellClick?: (row: T, e: React.MouseEvent) => void;
}

export function EriciusTable<T extends Record<string, any>>({
  columns,
  cols,
  data,
  getRowId,
  onRowClick,
  rowClassName,
  pagination,
  onPageChange,
  onPagePrev,
  onPageNext,
  onPageSizeChange,
  onPerPage,
  pageSizeOptions = [10, 25, 50, 100],
  sorting,
  onSortChange,
  isLoading = false,
  emptyMessage = 'No records found.',
  emptyState,
  minWidth = '650px',
  className = '',
}: EriciusTableProps<T>) {
  // Normalize columns from either `columns` or `cols` format
  const normalizedCols: NormalizedColumn<T>[] = useMemo(() => {
    const raw = columns || cols || [];
    return raw.map((item: any, index: number) => {
      const keys = Object.keys(item);
      // Check if item is { [colId]: { label, ... } }
      if (
        keys.length === 1 &&
        typeof item[keys[0]] === 'object' &&
        item[keys[0]] !== null &&
        !('label' in item)
      ) {
        const colKey = keys[0];
        const conf: EriciusColumnConfig<T> = item[colKey];
        return {
          key: colKey,
          label: conf.label,
          sortable: Boolean(conf.sortable),
          align: conf.align || 'left',
          width: conf.width,
          className: conf.className,
          headerClassName: conf.headerClassName,
          render:
            conf.render ||
            conf.colRenderer ||
            ((row: T) => (row[colKey] !== undefined ? String(row[colKey]) : null)),
          onCellClick: conf.onCellClick || conf.onColClick,
        };
      }

      // Standard Column object
      const colKey = item.key || item.id || `col_${index}`;
      return {
        key: colKey,
        label: item.label,
        sortable: Boolean(item.sortable),
        align: item.align || 'left',
        width: item.width,
        className: item.className,
        headerClassName: item.headerClassName,
        render:
          item.render ||
          item.colRenderer ||
          ((row: T) => (row[colKey] !== undefined ? String(row[colKey]) : null)),
        onCellClick: item.onCellClick || item.onColClick,
      };
    });
  }, [columns, cols]);

  const handleRowClick = (row: T, index: number, e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Don't trigger row click if user clicked a button, link, input, or explicit prevent element
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('select') ||
      target.closest('[data-prevent-row-click]') ||
      target.closest('.prevent-row-click')
    ) {
      return;
    }
    onRowClick?.(row, index);
  };

  const handleSort = (field: string) => {
    if (!onSortChange) return;
    if (sorting?.field === field) {
      onSortChange(field, sorting.dir === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(field, 'asc');
    }
  };

  // Pagination calculations
  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize))
    : 1;
  const currentPage = pagination ? pagination.page : 1;
  const fromIndex = pagination
    ? pagination.total === 0
      ? 0
      : (currentPage - 1) * pagination.pageSize + 1
    : 1;
  const toIndex = pagination
    ? Math.min(currentPage * pagination.pageSize, pagination.total)
    : data.length;

  const handlePageChange = (newPage: number) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else if (newPage > currentPage && onPageNext) {
      onPageNext();
    } else if (newPage < currentPage && onPagePrev) {
      onPagePrev();
    }
  };

  const handlePrevPage = () => {
    if (currentPage <= 1) return;
    if (onPagePrev) {
      onPagePrev();
    } else if (onPageChange) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage >= totalPages) return;
    if (onPageNext) {
      onPageNext();
    } else if (onPageChange) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageSizeSelect = (newSize: number) => {
    if (onPerPage) {
      onPerPage(newSize);
    } else if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <div
      className={`rounded-lg bg-cf-surface border border-cf-border overflow-hidden ${className}`}
    >
      <div className="overflow-x-auto">
        <table
          className="w-full text-left text-xs border-collapse"
          style={{ minWidth }}
        >
          <thead>
            <tr className="border-b border-cf-border/70 dark:border-[#2e323c] bg-cf-tableHeader dark:bg-[#2e323c] text-cf-textSecondary text-xs tracking-wider">
              {normalizedCols.map((col) => {
                const isSorted = sorting?.field === col.key;
                const alignClass =
                  col.align === 'right'
                    ? 'text-right'
                    : col.align === 'center'
                    ? 'text-center'
                    : 'text-left';

                return (
                  <th
                    key={col.key}
                    style={{ width: col.width }}
                    className={`py-2.5 px-4 font-medium uppercase text-xs tracking-wider text-cf-textSecondary select-none bg-[#f1f3f6] dark:bg-[#2e323c] bg-cf-tableHeader ${alignClass} ${
                      col.headerClassName || ''
                    } ${
                      col.sortable
                        ? 'cursor-pointer hover:text-cf-textPrimary transition-colors group/sort'
                        : ''
                    }`}
                    onClick={() => {
                      if (col.sortable) handleSort(col.key);
                    }}
                  >
                    <div
                      className={`inline-flex items-center gap-1.5 ${
                        col.align === 'right'
                          ? 'justify-end'
                          : col.align === 'center'
                          ? 'justify-center'
                          : 'justify-start'
                      }`}
                    >
                      <span>{col.label}</span>
                      {col.sortable && (
                        <span className="shrink-0">
                          {isSorted ? (
                            sorting.dir === 'asc' ? (
                              <ArrowUp size={12} className="text-cf-orange" />
                            ) : (
                              <ArrowDown size={12} className="text-cf-orange" />
                            )
                          ) : (
                            <ArrowUpDown
                              size={11}
                              className="text-cf-textMuted opacity-50 group-hover/sort:opacity-100 transition-opacity"
                            />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-y divide-cf-border/40">
            {isLoading ? (
              <tr>
                <td
                  colSpan={normalizedCols.length}
                  className="py-10 text-center text-xs"
                >
                  <div className="flex items-center justify-center gap-2 text-cf-textSecondary">
                    <Loader2 size={16} className="animate-spin text-cf-orange" />
                    <span>Loading data…</span>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={normalizedCols.length}
                  className="py-10 text-center text-cf-textMuted text-xs"
                >
                  {emptyState || emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                const rowId = getRowId
                  ? getRowId(row, rowIndex)
                  : row.id || `row_${rowIndex}`;
                const extraClass = rowClassName
                  ? rowClassName(row, rowIndex)
                  : '';

                return (
                  <tr
                    key={rowId}
                    onClick={(e) => handleRowClick(row, rowIndex, e)}
                    className={`bg-cf-surface transition-colors hover:bg-cf-surfaceHover group ${
                      onRowClick ? 'cursor-pointer' : ''
                    } ${extraClass}`}
                  >
                    {normalizedCols.map((col) => {
                      const alignClass =
                        col.align === 'right'
                          ? 'text-right'
                          : col.align === 'center'
                          ? 'text-center'
                          : 'text-left';

                      return (
                        <td
                          key={col.key}
                          className={`py-3 px-4 ${alignClass} ${col.className || ''}`}
                          onClick={(e) => {
                            if (col.onCellClick) {
                              col.onCellClick(row, e);
                            }
                          }}
                        >
                          {col.render(row, rowIndex)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Optional Pagination Footer */}
      {pagination && (
        <DataGridPagination
          page={currentPage}
          pageSize={pagination.pageSize}
          totalRows={pagination.total}
          pageSizeOptions={pageSizeOptions}
          unitLabel="results"
          showPageSize={Boolean(onPerPage || onPageSizeChange)}
          onPageChange={handlePageChange}
          onPageSizeChange={onPerPage || onPageSizeChange ? (s) => handlePageSizeSelect(s) : undefined}
        />
      )}
    </div>
  );
}
